import { Injectable } from '@angular/core'
import { Observable, Subscription } from 'rxjs'
import { shareReplay, take } from 'rxjs/operators'
import { Footnote } from 'src/app/models/footnote.model'
import { AEMChunk } from 'src/app/services/AEM/aem-chunk.model'
import { CentralContentService } from 'src/app/services/content/central-content.service'
import { FootnoteService } from 'src/app/services/footnotes/footnotes.service'
import { FundAnalyticsService } from 'src/app/services/fund-analytics/fund-analytics.service'
import { MainService } from 'src/app/services/main.service'
import { MarketDataService } from 'src/app/services/market-data/market-data.service'
import { PremiumDiscountSummaryService } from 'src/app/services/premium-discount-summary/premium-discount-summary.service'

@Injectable({
  providedIn: 'root',
})
export class PriceTabService {
  priceModel: any
  priceModel$: Observable<any>
  premiumDiscountModel: any
  premiumDiscountModelModel$: Observable<any>
  fundAnaliticsModel: any
  fundAnaliticsModel$: Observable<any>
  premiumDiscount: object[]
  aemContentSub: Subscription
  secYieldNonVgContent: AEMChunk
  secYieldNonVgFootNote: Footnote
  // eslint-disable-next-line complexity, max-params
  constructor(
    private readonly mainService: MainService,
    private readonly marketDataService: MarketDataService,
    private readonly premiumDiscountSummaryService: PremiumDiscountSummaryService,
    private readonly fundAnalyticsService: FundAnalyticsService,
    private readonly footnoteService: FootnoteService,
    private readonly centralContentService: CentralContentService,
  ) {}
  /* istanbul ignore next */
  getModelData(): Observable<any> {
    const marketPriceContentIndex = 3

    return new Observable((subscriber) => {
      this.mainService
        .getPriceObservable()
        .pipe(take(1))
        .subscribe((price) => {
          if (
            Object.keys(price).length !== 0 &&
            price[0]?.name !== 'HTTPError' &&
            this.priceModel === undefined
          ) {
            this.priceModel = {
              profile: price[0]?.fundProfile,
              price: price[1],
              // eslint-disable-next-line @typescript-eslint/no-magic-numbers
              content: price[2],
              marketPriceContent: price[marketPriceContentIndex],
            }
            if (price[0]?.fundProfile?.isETF) {
              this.marketDataService
                .getMarketPrice(price[0]?.fundProfile?.ticker)
                .pipe(shareReplay())
                .subscribe((response) => {
                  this.priceModel.delayedPrice = response
                })
            }
          }
          this.setFootnotes()
          subscriber.next(this.priceModel)
          subscriber.complete()
        })
    })
  }
  /* istanbul ignore next */
  getFundAnalytics(): Observable<any> {
    return new Observable((subscriber) => {
      this.mainService
        .getProfileObservable()
        .pipe(take(1))
        .subscribe((profile) => {
          if (profile.fundProfile.isETF && profile.fundProfile.isInternalFund) {
            this.fundAnalyticsService
              .getFundAnalytics(profile.fundProfile.fundId)
              .pipe(shareReplay())
              .subscribe((response) => {
                response?.forEach((element) => {
                  if (element.analyticCode === '30DMDBDASK') {
                    this.fundAnaliticsModel = element.analyticValue
                  }
                })

                subscriber.next(this.fundAnaliticsModel)
                subscriber.complete()
              })
          }
        })
    })
  }
  // eslint-disable-next-line complexity, max-statements
  premiumLabel(elemantlabel: string, asOfDt: number) {
    let label = ''
    if (elemantlabel === 'CURR') {
      label = 'Current qtr'
    } else if (elemantlabel === '3Qtr') {
      label = `Q3 ${asOfDt}`
    } else if (elemantlabel === '2Qtr') {
      label = `Q2 ${asOfDt}`
    } else if (elemantlabel === '4Qtr') {
      label = `Q4 ${asOfDt}`
    } else if (elemantlabel === '1Qtr') {
      label = `Q1 ${asOfDt}`
    } else if (elemantlabel === 'YEAR') {
      label = `Calendar year ${asOfDt}`
    }
    return label
  }

  //premium Discount
  /* istanbul ignore next */
  getPremiumData(): Observable<any> {
    return new Observable((subscriber) => {
      this.mainService
        .getProfileObservable()
        .pipe(take(1))
        .subscribe((profile) => {
          if (profile.fundProfile.isETF) {
            this.premiumDiscountSummaryService
              .getPremiumDisCountSummary(profile.fundProfile.fundId)
              .pipe(shareReplay())
              .subscribe((response) => {
                this.premiumDiscount = []
                response.premiumDiscountByPeriod.forEach((element) => {
                  const date = new Date(element.asOfDt).getFullYear()
                  const label = this.premiumLabel(element.prdLabel, date)
                  this.premiumDiscount.push({ label, element })
                })
                subscriber.next(this.premiumDiscount)
                subscriber.complete()
              })
          }
        })
    })
  }

  private setFootnotes() {
    this.aemContentSub = this.centralContentService.aemContent$.subscribe(
      (content) => {
        if (
          this.priceModel.profile.isExternalFund &&
          !this.priceModel.profile.isETF
        ) {
          this.secYieldNonVgContent =
            this.centralContentService.getContentByBody(
              'footnote_non_vanguard_sec_yield',
              content,
            )
          this.priceModel.secYieldNonVgFootNote = new Footnote(
            this.secYieldNonVgContent.contentValue,
            'price',
            true,
            'footnote_non_vanguard_sec_yield',
          )
          this.footnoteService.addFootnote(
            this.priceModel.secYieldNonVgFootNote,
          )
        }
      },
    )
    if (this.priceModel?.content?.etfSplit) {
      this.priceModel.etfSplitFootnote = new Footnote(
        this.priceModel?.content.etfSplit,
        'price',
        false,
        'etf_split',
      )
      this.footnoteService.addFootnote(this.priceModel.etfSplitFootnote)
    }
  }
}
